import * as React from "react";
import { Box, Card, Chip, Stack, Typography } from "@mui/material";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import SensorsRoundedIcon from "@mui/icons-material/SensorsRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { alpha } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import { COLORS } from "../theme/theme-options";

type ComingSoonContent = {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  accent: string;
  items: string[];
};

const contentByPath: Record<string, ComingSoonContent> = {
  "/event-analytics": {
    title: "Event Analytics",
    subtitle: "Advanced event intelligence is on the way",
    icon: <BarChartRoundedIcon />,
    accent: "#22d3ee",
    items: [
      "Timeline and event clustering",
      "Correlation across services and alerts",
      "Pattern-based anomaly detection",
    ],
  },
  "/war-room": {
    title: "War Room",
    subtitle: "Collaborative incident operations are coming soon",
    icon: <SensorsRoundedIcon />,
    accent: "#34d399",
    items: [
      "Shared incident timeline",
      "Live collaboration with role ownership",
      "Automated status updates and handoff notes",
    ],
  },
};

const defaultContent: ComingSoonContent = {
  title: "Coming Soon",
  subtitle: "This area is currently in development",
  icon: <AutoAwesomeRoundedIcon />,
  accent: "#60a5fa",
  items: [
    "New capabilities are being implemented",
    "Design and workflow are being finalized",
    "This page will be available in a future update",
  ],
};

const ComingSoonPage: React.FC = () => {
  const { pathname } = useLocation();
  const content = contentByPath[pathname] ?? defaultContent;

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: 420,
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(1200px 320px at 20% -20%, rgba(56,189,248,0.18), transparent 70%), radial-gradient(1000px 280px at 80% 120%, rgba(16,185,129,0.12), transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Card
        variant="glass"
        sx={{
          p: { xs: 2.4, md: 3.2 },
          width: "100%",
          maxWidth: 760,
          border: `1px solid ${alpha(content.accent, 0.35)}`,
          boxShadow: `0 16px 48px ${alpha(content.accent, 0.12)}`,
          position: "relative",
        }}
      >
        <Stack spacing={2.2}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                color: content.accent,
                border: `1px solid ${alpha(content.accent, 0.45)}`,
                background: `linear-gradient(135deg, ${alpha(content.accent, 0.22)}, ${alpha(content.accent, 0.08)})`,
              }}
            >
              {content.icon}
            </Box>
            <Chip
              size="small"
              label="Coming Soon"
              sx={{
                color: content.accent,
                bgcolor: alpha(content.accent, 0.12),
                border: `1px solid ${alpha(content.accent, 0.32)}`,
                fontWeight: 700,
              }}
            />
          </Stack>

          <Box>
            <Typography sx={{ fontSize: 30, fontWeight: 800, color: COLORS.grey[100] }}>
              {content.title}
            </Typography>
            <Typography sx={{ mt: 0.7, color: COLORS.grey[400], fontSize: 18 }}>
              {content.subtitle}
            </Typography>
          </Box>

          <Stack spacing={1.1}>
            {content.items.map((item) => (
              <Stack key={item} direction="row" spacing={1.1} alignItems="center">
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: content.accent,
                    boxShadow: `0 0 12px ${alpha(content.accent, 0.45)}`,
                    flex: "0 0 auto",
                  }}
                />
                <Typography sx={{ color: COLORS.grey[300] }}>{item}</Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Card>
    </Box>
  );
};

export default ComingSoonPage;
